import { useEffect, useState } from 'react';
import {
    useDealerStep,
    useOSBStep,
    useVehicleStep,
} from '../../../../../hooks/owners-osb';
import PreferredServiceCentre from '../preferred-service-centre/preferred-service-centre';
import SearchServiceCentre from '../search-service-centre/search-service-centre';
import { useLocation } from 'react-router-dom';
import useMarketDealerInfoService from '../../../../../hooks/owners-osb/use-market-dealerInfo-service';
import {
    AllOsbDealersResponse,
    DealerProfileInfo,
} from '../../../../../models/osb-model/osb-dealer-info';
import PreferredServiceCentreSkeleton from '../preferred-service-centre-skeleton/preferred-service-centre-skeleton';
import ErrorDisplay from '../../common/error-display/error-display';
import { useOSBContentFragment } from '../../../../../hooks/owners-osb/aem-content-util/use-osb-content-fragment';
import BookingLookupSection from '../../booking-lookup-section/booking-lookup-section';

const ServiceHandler = () => {
    const { osbVehicleStep } = useVehicleStep();
    const { setOSBStepPayload } = useOSBStep();
    const { setOSBDealerStepPayload } = useDealerStep();
    const { fetchMarketDealerInfo } = useMarketDealerInfoService();
    const deepLinkParams = new URLSearchParams(useLocation().search);
    const osbContentData =
        useOSBContentFragment('common/osb-v3', 'service-centre-step') || {};
    const osbConfigData =
        useOSBContentFragment('common/osb-v3', 'configuration') || {};

    const [dealerInfo, setDealerInfo] = useState<AllOsbDealersResponse | null>(
        null
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showPreferred, setShowPreferred] = useState(true);

    useEffect(() => {
        const fetchDealers = async () => {
            try {
                const response = await fetchMarketDealerInfo(deepLinkParams);
                if (response) {
                    setDealerInfo(response);
                    setError(null);
                } else {
                    setDealerInfo(null);
                    setError(
                        osbContentData?.serviceCentreErrorMessage as string
                    );
                }
            } catch (error) {
                setError(osbContentData?.serviceCentreErrorMessage as string);
            } finally {
                setLoading(false);
            }
        };

        fetchDealers();
    }, []);

    const preferredDealerCode = osbVehicleStep?.vehicleDetail?.preferredDealer;

    const dealerCodePrefixLength =
        parseInt(String(osbConfigData?.dealerCodePrefixLength), 10) || 0;
    const trimmedPreferredDealerCode = preferredDealerCode
        ? preferredDealerCode.slice(dealerCodePrefixLength)
        : null;

    const preferredDealer = dealerInfo?.dealers.find(
        dealer => dealer.dealerCode === trimmedPreferredDealerCode
    );
    const handleSearchDealerClick = () => {
        setShowPreferred(false);
    };

    const handlePreferredDealerClick = () => {
        setShowPreferred(true);
    };

    const handleSelectPreferredDealerClick = (
        selectedDealerProfile: DealerProfileInfo
    ) => {
        // Implement the logic for selecting the preferred dealer here
        setOSBStepPayload({
            preferredDealerJourney: true,
        });

        setOSBDealerStepPayload({
            selectedDealerProfile: selectedDealerProfile,
        });
    };

    return (
        <>
            <div
                data-testid="service-centre-handler"
                className="osb-v3-service-handler"
            >
                {loading ? (
                    <PreferredServiceCentreSkeleton />
                ) : error ? (
                    <ErrorDisplay
                        title={`${osbContentData?.serviceCentreErrorTitle}`}
                        errorMessage={error}
                        suggestion={`${osbContentData?.serviceCentreErrorSuggestion}`}
                    />
                ) : showPreferred && preferredDealer ? (
                    <PreferredServiceCentre
                        preferredServiceCentre={preferredDealer}
                        onSearchDealerClick={handleSearchDealerClick}
                        onSelectPreferredDealerClick={
                            handleSelectPreferredDealerClick
                        }
                    />
                ) : (
                    <SearchServiceCentre
                        onPreferredDealerClick={handlePreferredDealerClick}
                        hasPreferredDealer={!!preferredDealer}
                    />
                )}
            </div>
            <BookingLookupSection className="booking-lookup-offset--visual" />
        </>
    );
};

export default ServiceHandler;
